import React, { useState, useEffect } from "react";
import { Upload, FileText, Settings, CheckCircle, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ProjectSetupPhase } from "@/types";
import { useLocation } from "wouter";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import PartyCelebration from "@/components/ui/party-celebration";
import AsyncProgress from "@/components/ui/async-progress";
import { createProjectExtractionCheckpoints } from "@/lib/progress-checkpoints";
import { useBackgroundJobStatus } from "@/hooks/use-background-job-status";

interface ProjectNextActionCardProps {
  projectId: string;
  currentPhase: ProjectSetupPhase;
  hasDocuments: boolean;
  hasContract: boolean;
  contractExtracted: boolean;
  documentsConfirmed?: boolean;
  projectStatus?: string; // Add project status prop
  triggerReExtraction?: boolean;
  onReExtractionStart?: () => void;
}

export default function ProjectNextActionCard({
  projectId,
  currentPhase,
  hasDocuments,
  contractExtracted,
  documentsConfirmed = false,
  projectStatus = "setup",
  triggerReExtraction = false,
  onReExtractionStart,
}: ProjectNextActionCardProps) {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [isExtracting, setIsExtracting] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [processingDocuments, setProcessingDocuments] = useState<Array<{fileName: string, documentType: string}>>([]);
  const [processingStartTime, setProcessingStartTime] = useState<Date | null>(null);
  const [_backgroundJobId, setBackgroundJobId] = useState<string | null>(null);

  // Use background job status hook to track real progress
  const { jobs: _jobs, activeJobs, overallStatus: _overallStatus } = useBackgroundJobStatus({
    entityId: projectId,
    entityType: 'project',
    onComplete: (job) => {
      console.log('Background job completed:', job);
      setIsExtracting(false);
      setProcessingDocuments([]);
      setProcessingStartTime(null);
      setBackgroundJobId(null);
      
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: [`/api/projects/${projectId}`] });
      queryClient.invalidateQueries({ queryKey: [`/api/projects/${projectId}/documents/analysis`] });
      
      toast({
        title: "Contract details extracted",
        description: "Contract details have been successfully extracted from your documents."
      });
    },
    onFailed: (job) => {
      console.log('Background job failed:', job);
      setIsExtracting(false);
      setProcessingDocuments([]);
      setProcessingStartTime(null);
      setBackgroundJobId(null);
      
      toast({
        title: "Contract extraction failed",
        description: job.error_message || "Processing failed. Please try again.",
        variant: "destructive"
      });
    }
  });

  // Auto-show celebration when contract is extracted and project becomes active
  useEffect(() => {
    if (contractExtracted && projectStatus === "active") {
      setShowCelebration(true);
    }
  }, [contractExtracted, projectStatus]);

  // Sync isExtracting state with background job status
  useEffect(() => {
    if (activeJobs.length > 0) {
      setIsExtracting(true);
    } else if (_overallStatus === 'completed' || _overallStatus === 'failed') {
      setIsExtracting(false);
    }
  }, [activeJobs.length, _overallStatus]);


  // Mutation to confirm documents
  const confirmDocumentsMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("PUT", `/api/projects/${projectId}`, {
        documentsConfirmed: true
      });
      return response.json();
    },
    onSuccess: () => {
      // Invalidate project queries to refresh the data
      queryClient.invalidateQueries({ queryKey: [`/api/projects/${projectId}`] });
      queryClient.invalidateQueries({ queryKey: [`/api/projects/${projectId}/documents/analysis`] });
      
      toast({
        title: "Documents confirmed",
        description: "Starting contract extraction automatically..."
      });
      
      // Auto-trigger extraction after confirmation
      setTimeout(() => {
        extractContractDetailsMutation.mutate();
      }, 500); // Small delay to allow UI to update
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to confirm documents",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // Mutation to extract contract details
  const extractContractDetailsMutation = useMutation({
    mutationFn: async () => {
      console.log("🚀 EXTRACT CONTRACT DETAILS MUTATION STARTED", { projectId });
      setIsExtracting(true);
      setProcessingStartTime(new Date());
      
      // First, get the documents for processing display
      console.log("📄 Fetching documents for display");
      const documentsResponse = await apiRequest("GET", `/api/projects/${projectId}/documents`);
      const documentsData = await documentsResponse.json();
      
      // Set processing documents for display
      setProcessingDocuments(documentsData.map((doc: {fileName: string, documentType?: string}) => ({
        fileName: doc.fileName,
        documentType: doc.documentType || 'Contract'
      })));
      
      console.log("🔥 Making POST request to extract-contract-details");
      const response = await apiRequest("POST", `/api/projects/${projectId}/extract-contract-details`);
      const result = await response.json();
      console.log("✅ Extract contract details response:", result);
      
      // Store the background job ID for tracking
      if (result.background_job_id) {
        setBackgroundJobId(result.background_job_id);
      }
      
      return result;
    },
    onSuccess: () => {
      toast({
        title: "Contract processing started",
        description: "Contract details are being extracted from your documents. Progress will be tracked automatically."
      });
    },
    onError: (error: Error) => {
      setIsExtracting(false);
      setProcessingDocuments([]);
      setProcessingStartTime(null);
      
      toast({
        title: "Failed to extract contract details",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // Mutation to activate project and transition from setup to active status
  const activateProjectMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("PUT", `/api/projects/${projectId}`, {
        status: "active"
      });
      return response.json();
    },
    onSuccess: () => {
      // Invalidate project queries to refresh the data
      queryClient.invalidateQueries({ queryKey: [`/api/projects/${projectId}`] });
      queryClient.invalidateQueries({ queryKey: ["/api/projects"] }); // Also refresh project list
      
      toast({
        title: "Project activated",
        description: "Project setup is complete! You can now manage claims."
      });
      
      // User stays on the same project page - it will automatically show active layout
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to activate project",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // Handle celebration completion
  const handleCelebrationComplete = () => {
    setShowCelebration(false);
    // Auto-activate project after celebration
    activateProjectMutation.mutate();
  };

  // Handle trigger re-extraction
  useEffect(() => {
    if (triggerReExtraction && !isExtracting) {
      console.log("🔄 Triggering re-extraction from ProjectNextActionCard");
      extractContractDetailsMutation.mutate();
      onReExtractionStart?.();
    }
  }, [triggerReExtraction, isExtracting, extractContractDetailsMutation, onReExtractionStart]);

  // Don't render anything if project is already active
  if (projectStatus === "active") {
    return null;
  }

  // Determine next action based on current state
  const getNextAction = () => {
    console.log("🔍 DETERMINING NEXT ACTION:", {
      hasDocuments,
      documentsConfirmed,
      currentPhase,
      contractExtracted,
      projectId
    });
    
    if (!hasDocuments) {
      console.log("📤 No documents - showing upload prompt");
      return {
        title: "Upload Project Documents",
        description: "Start by uploading your contract and compliance documents to enable analysis and claim management.",
        icon: Upload,
        priority: "high" as const,
        isInformational: true // Make this informational only, no button
      };
    }
    
    // Show confirmation prompt when documents are uploaded but not confirmed
    if (hasDocuments && !documentsConfirmed) {
      console.log("✅ Documents exist but not confirmed - showing confirm button");
      return {
        title: "Confirm Document Upload",
        description: "Confirm that contract and compliance documents have been added to proceed to next step. You will be able to add other documents later.",
        action: "Confirm",
        icon: CheckCircle,
        onClick: () => confirmDocumentsMutation.mutate(),
        priority: "high" as const,
        isConfirmation: true
      };
    }
    
    // When documents are confirmed, show informational message that extraction is happening
    if (hasDocuments && documentsConfirmed && !contractExtracted) {
      console.log("🎯 Documents confirmed - extraction in progress or will start automatically");
      return {
        title: "Extracting Contract Details",
        description: "AI analysis is processing your uploaded documents to extract project details. This will happen automatically.",
        icon: FileText,
        priority: "high" as const,
        isInformational: true // No button needed since it auto-triggers
      };
    }
    
    // When contract is extracted, don't show the setup complete card anymore
    // The celebration will handle the activation
    if (contractExtracted) {
      return null; // Return null to hide the card
    }
    
    return {
      title: "Complete Project Setup",
      description: "Finish setting up your project by uploading documents and extracting contract details.",
      action: "Continue Setup",
      icon: Settings,
      onClick: () => navigate(`/projects/${projectId}/documents`),
      priority: "medium" as const
    };
  };

  const nextAction = getNextAction();
  
  // Always check for active jobs first, regardless of next action
  const hasActiveJob = activeJobs.length > 0 || isExtracting;
  const activeJob = activeJobs.find(job => job.entity_type === 'project') || null;
  
  // Show processing status if contract extraction is in progress or there are active background jobs
  if (hasActiveJob) {
    const checkpoints = createProjectExtractionCheckpoints(processingDocuments.length);
    
    // Get current checkpoint from background job if available
    const currentCheckpointId = (activeJob?.processing_data && typeof activeJob.processing_data === 'object' && 'current_checkpoint' in activeJob.processing_data && typeof activeJob.processing_data.current_checkpoint === 'string') ? activeJob.processing_data.current_checkpoint : 'initializing';
    const jobStartTime = activeJob?.started_at ? new Date(activeJob.started_at) : processingStartTime;
    
    // Map background job status to AsyncProgress status
    const progressStatus = activeJob ? activeJob.status as 'pending' | 'processing' | 'completed' | 'failed' | 'retry' : 'processing';
    
    return (
      <div className="space-y-6">
        <AsyncProgress
          status={progressStatus}
          workflowTitle="Extracting Contract Details"
          workflowDescription="AI agents are analyzing contract documents to extract project information including scope, amounts, and key terms."
          checkpoints={checkpoints}
          currentCheckpointId={currentCheckpointId}
          startTime={jobStartTime || undefined}
          documents={processingDocuments}
          onCancel={async () => {
            try {
              const response = await apiRequest("DELETE", `/api/projects/${projectId}/cancel-extraction`);
              const result = await response.json();
              
              setIsExtracting(false);
              setProcessingDocuments([]);
              setProcessingStartTime(null);
              setBackgroundJobId(null);
              
              // Invalidate queries to refresh data
              queryClient.invalidateQueries({ queryKey: [`/api/projects/${projectId}`] });
              queryClient.invalidateQueries({ queryKey: [`/api/projects/${projectId}/documents/analysis`] });
              
              toast({
                title: "Processing cancelled",
                description: result.message || "Contract extraction has been cancelled successfully.",
              });
            } catch (error) {
              console.error("Failed to cancel extraction:", error);
              toast({
                title: "Failed to cancel",
                description: "Could not cancel the extraction process. Please try again.",
                variant: "destructive"
              });
            }
          }}
          showAIIndicator={true}
        />
        
        {/* Party Celebration Overlay */}
        <PartyCelebration 
          show={showCelebration} 
          onComplete={handleCelebrationComplete}
        />
      </div>
    );
  }
  
  // If no action needed and no active job, don't render the card
  if (!nextAction) {
    return (
      <>
        {/* Party Celebration Overlay */}
        <PartyCelebration 
          show={showCelebration} 
          onComplete={handleCelebrationComplete}
        />
      </>
    );
  }

  const Icon = nextAction.icon;

  const priorityStyles = {
    high: "bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200",
    medium: "bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200",
    low: "bg-gradient-to-r from-green-50 to-emerald-50 border-green-200"
  };

  const iconStyles = {
    high: "text-blue-600",
    medium: "text-amber-600",
    low: "text-green-600"
  };

  // Use green styling for confirmation prompt
  const cardStyle = nextAction.isConfirmation 
    ? "bg-gradient-to-r from-green-50 to-emerald-50 border-green-200"
    : priorityStyles[nextAction.priority];
    
  const iconStyle = nextAction.isConfirmation 
    ? "text-green-600"
    : iconStyles[nextAction.priority];

  return (
    <>
      {/* Party Celebration Overlay */}
      <PartyCelebration 
        show={showCelebration} 
        onComplete={handleCelebrationComplete}
      />
      
      {/* Next Action Card */}
      <Card className={`${cardStyle} mb-4`}>
        <CardContent className="p-4">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-full bg-white shadow-sm ${iconStyle}`}>
              <Icon className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <h3 className="text-base font-semibold text-gray-900 mb-1">
                {nextAction.title}
              </h3>
              <p className={`text-sm text-gray-600 ${nextAction.isInformational ? 'mb-0' : 'mb-3'}`}>
                {nextAction.description}
              </p>
              {/* Only show button if it's not an informational card */}
              {!nextAction.isInformational && (
                <Button 
                  onClick={nextAction.onClick}
                  size="sm"
                  className="flex items-center space-x-2"
                  disabled={confirmDocumentsMutation.isPending || isExtracting || activateProjectMutation.isPending}
                >
                  {isExtracting ? (
                    <>
                      <Loader2 className="animate-spin w-4 h-4" />
                      <span>Extracting...</span>
                    </>
                  ) : activateProjectMutation.isPending ? (
                    <>
                      <Loader2 className="animate-spin w-4 h-4" />
                      <span>Activating...</span>
                    </>
                  ) : (
                    <>
                      <span>
                        {confirmDocumentsMutation.isPending ? "Confirming..." : nextAction.action}
                      </span>
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
} 